import L from "leaflet";
import * as turf from "@turf/turf";
import type { Polygon, Position } from "geojson";
import {
  fx_redondearCoordenada,
  fx_truncarPolygon,
} from "./AoiPolygonUnion";
import { AoiPiezasState } from "./AoiPiezasState";
import { MapSnap } from "./MapSnap";

/** Motor de dibujo del AOI (paso 3 del wizard de registro de finca).
 *  **Sin `leaflet-draw`**: el dibujo se resuelve con eventos nativos de
 *  Leaflet, misma metodología que la app de referencia externa
 *  (`referencias_codigo_externo/App`, `src/proyectos/lib/dibujoMapa.ts` —
 *  SCANOVA). No conoce la UI: se instancia con el mapa y lo maneja
 *  `AoiDrawToolbar`, igual que allá el motor no conoce a `BarraDibujo`.
 *
 *  **Solo polígono.** La referencia ofrece 5 herramientas (punto, línea,
 *  polígono, rectángulo, círculo); acá se define el AOI de una finca, así que
 *  se porta únicamente el polígono. Por eso tampoco se escucha `dblclick`,
 *  que allá solo servía para iniciar rectángulo/círculo.
 *
 *  Mecánica del trazo (idéntica a la referencia):
 *   - click en el mapa → agrega un vértice;
 *   - click en el PRIMER vértice → cierra el polígono (ese vértice lleva el
 *     indicador "radar" que ya usaba el paso 3, ahora como objetivo real);
 *   - el cursor arrastra una guía en vivo hasta el próximo vértice.
 *
 *  **La herramienta NO se suelta al cerrar una forma**: queda activa para
 *  encadenar la siguiente (comportamiento de la referencia; el paso 3 antes
 *  se apagaba solo tras cada polígono). Se suelta al volver a pulsar su botón.
 *
 *  Dos cosas que la referencia NO tiene y acá sí, ambas para no perder
 *  funcionalidad que el paso 3 ya daba con `leaflet-draw`:
 *   - **Editar**: lo resuelve `AoiSnapEditEngine`, no este motor. Acá sólo se
 *     detecta sobre QUÉ pieza se hizo click (`_alClickDePieza`) y se avisa con
 *     `onPedirEdicion`. La referencia no tiene editar — corregir es borrar y
 *     redibujar.
 *   - **Anti-autointersección** (`_seCruza`, con `turf.kinks`): lo daba
 *     `allowIntersection: false` de `L.Draw.Polygon`. Sin esto el paso 3
 *     aceptaría polígonos cruzados que hoy rechaza y que rompen la unión
 *     topológica (`AoiPolygonUnion`).
 *
 *  **Lo que ya es definitivo se rechaza en el acto, vértice a vértice**
 *  (`_invadeOtraPieza` para las piezas vecinas, `_cruzaElTrazo` para el
 *  propio trazo), y la guía en vivo se pinta roja cuando soltar ahí sería
 *  inválido: si el rechazo llega recién al cerrar, el usuario trazó el
 *  polígono entero para nada.
 *
 *  **Definitivo** quiere decir algo que el click deja fijo y que ningún vértice
 *  posterior puede arreglar: dónde cae el vértice, y el tramo que lo une al
 *  anterior. La FORMA provisional —el anillo que saldría de cerrar el trazo
 *  ahora— no cuenta: cambia con cada vértice, y usarla como validador bloquea
 *  trazos válidos (rodear una pieza vecina pasa necesariamente por estados
 *  intermedios que la pisan). Por eso el chequeo por área vive solo en
 *  `_cerrarPoligono`, con la forma ya cerrada.
 *
 *  **El reparto entre los dos motores (2026-10-23).** Leaflet dibuja lo que el
 *  usuario está manipulando AHORA; GeoServer dibuja lo que ya está guardado.
 *
 *   - **El trazo en curso es de Leaflet, y sólo de Leaflet.** Cada click
 *     agrega un vértice a `_vertices` —memoria del cliente, nada más— y
 *     `_repintarPrevia` redibuja la guía en el acto, sin red de por medio: es
 *     el motor de Leaflet dibujando rápido. Hasta el 2026-10-23 cada click
 *     viajaba al servidor (`AgregarVerticeTrazo`) y el trazo lo renderizaba
 *     GeoServer desde `tbl_temp_vertices_dibujo` + `vw_dibujo_en_curso`: un
 *     viaje de red y una tanda de tiles por vértice, y el dibujo se veía
 *     siempre un paso atrás de la mano. Con la guía local vuelve además la
 *     banda elástica entre el último vértice y el cursor, que GeoServer no
 *     podía dar porque la posición del cursor no está guardada en ningún lado.
 *
 *   - **Se dibuja UN polígono a la vez.** La guía es la del trazo abierto; las
 *     piezas ya cerradas no se repintan en Leaflet.
 *
 *   - **Ninguna pieza ya guardada**: no se guardan como capas de Leaflet ni se
 *     pintan con Leaflet. Viven en `AoiPiezasState` como GeoJSON —en memoria,
 *     SIN renderizar— y las dibuja la capa WMS de GeoServer
 *     (`vw_poligonos_padre`), que es la única que las renderiza. Esas
 *     coordenadas en memoria son las que usan los chequeos de solape y de
 *     cruce (`_invadeOtraPieza`, `_piezaQueContiene`, `_segmentoAtraviesa`):
 *     están para validar, no para pintar.
 *
 *  Este motor dibuja entonces tres cosas y nada más: el trazo en curso, el
 *  parpadeo rojo de un trazo que se cruza, y la pieza recién cerrada mientras
 *  el guardado viaja (`_pintarEnCreacion`, hasta `confirmarPersistencia`).
 *  Cerrada la forma, el polígono sale por `onDibujado`/`onTrazo`, lo guarda el
 *  servidor y pasa a pintarlo el WMS.
 *
 *  Tampoco hay arrastre de vértices: eso es `AoiSnapEditEngine`, que trabaja
 *  sobre la geometría leída de la base (gRPC) y pone tres handles por tramo. */

/** Naranja "sin seleccionar" del mapa del paso 3 — el mismo de los contornos
 *  de departamento (`AoiMapRender`) y de los lotes (`BaseMapRender`). */
const COLOR_AOI = "rgb(224, 73, 13)";

/** Rojo de "esto se va a borrar" / "esto está mal", ya usado por el paso 3
 *  (era el `drawError` de leaflet-draw) y por la línea de corte del paso 4. */
const COLOR_ERROR = "#e1004c";

/** Estilo del polígono dibujado, en reposo. */
export const POLYGON_STYLE: L.PathOptions = {
  color: COLOR_AOI,
  weight: 3,
  opacity: 1,
  fillColor: COLOR_AOI,
  fillOpacity: 0.25,
};

/* `EDIT_STYLE`, `HIGHLIGHT_STYLE` y `DELETE_SELECTED_STYLE` se eliminaron:
   eran estilos que este motor le aplicaba a una pieza YA GUARDADA, y una pieza
   guardada ya no se pinta en Leaflet (la pinta sólo el WMS, ver
   `AoiPiezasState`).

   De los tres, el único que tiene equivalente es el de borrar:
   `sextante_poligono_padre_eliminar.sld` (el mismo rojo #E1004C que tenía
   `DELETE_SELECTED_STYLE`), aplicado sobre una capa WMS filtrada a esa pieza.

   `EDIT_STYLE` no tiene reemplazo: seleccionar una pieza para editarla ya no la
   repinta de ningún color. Lo que señala cuál se está editando son sus
   VÉRTICES, que son una capa WMS propia (`tbl_temp_vertices_dibujo`, filtrada
   por `poligono_padre_id`). Hubo un SLD celeste para esto y se quitó: costaba
   un GetMap de la pieza entera por cada click de selección para decir lo que
   los vértices dicen mejor.

   `HIGHLIGHT_STYLE` —el amarillo del hover en modo Borrar— tampoco: por WMS
   sería un GetMap por cada `mousemove`. En Borrar, el resalte pasó a ser al
   click, en rojo, justo antes de la confirmación. */

/** Vértice del trazo en curso. Es un `L.DivIcon` (un `<div>` del DOM), NO un
 *  `L.circleMarker`: los marcadores del DOM viven en `markerPane` y se pintan
 *  y se clickean sin depender de renderers ni de panes propios — que es
 *  exactamente donde fallaba la primera versión de este motor (ver el
 *  comentario de `PANE_AOI` arriba).
 *
 *  **El ícono de los vértices intermedios se eliminó.** Al dibujar, sólo el
 *  primer vértice lleva marcador (el de cierre): un `L.marker` por click hacía
 *  que el trazo se pusiera más pesado con cada punto. Ver `_agregarVertice`.
 *  La clase `custom-draw-vertex` de `aoiMap.css` sigue usándose en el modo
 *  editar y en el paso 4, así que no se toca. */
/** Vértice de CIERRE (el primero). MISMO tamaño que el resto de los vértices
 *  (14px): se probó más grande, a 20px, y desentonaba con los demás puntos.
 *  Lo distingue el color amarillo —el de "seleccionado" de este mapa— y sobre
 *  todo el radar que pulsa en esa misma posición, que ya deja clarísimo dónde
 *  hay que volver para cerrar la forma. */
/** Indicador del snap mientras se DIBUJA: idéntico a un vértice del trazo.
 *
 *  **Es la misma clase y el mismo tamaño que los demás vértices del dibujo**, y
 *  eso es el punto. Acá estuvo puesto `VTX_ICON` —el handle verde de los motores
 *  de edición— y mezclaba dos lenguajes en la misma herramienta: el trazo lo
 *  dibuja GeoServer blanco con borde naranja (`sextante_vertices_dibujo.sld`,
 *  `#E0490D`) y el punto enganchado aparecía verde con cursor de "agarrar", como
 *  si se pudiera arrastrar. Dibujando no se arrastra nada: cada click deja un
 *  vértice, y todos los vértices del trazo son el mismo tipo de cosa, así que se
 *  ven igual.
 *
 *  Lo que avisa del enganche no es el color sino la POSICIÓN: el punto salta del
 *  cursor al vértice existente. */
const SNAP_DIBUJO_ICON = new L.DivIcon({
  iconSize: [14, 14],
  className: "leaflet-div-icon custom-draw-vertex",
});

const VERTICE_CIERRE_ICON = new L.DivIcon({
  iconSize: [14, 14],
  className: "leaflet-div-icon custom-draw-vertex custom-draw-vertex--cierre",
});

/** Estilo de los vértices YA PUESTOS del trazo: círculo blanco con borde
 *  naranja. Es el port exacto de `.custom-draw-vertex` del CSS —el mismo punto
 *  que usan el snap y los motores de edición—, pero dibujado como círculo y no
 *  como `L.DivIcon`.
 *
 *  **Por qué círculo y no un marcador del DOM.** Un `L.Marker` por vértice es
 *  un nodo del DOM con su listener, que Leaflet reposiciona en cada zoom, pan y
 *  movimiento del cursor: así estaba antes y por eso se habían quitado —
 *  dibujando un perímetro de decenas de vértices cada click costaba más que el
 *  anterior. Un `L.circleMarker` se pinta en el canvas del pane
 *  (`preferCanvas: true` en los dos mapas del wizard), sin nodo propio ni
 *  listeners, así que el costo por vértice es una primitiva de dibujo y no
 *  crece en complejidad.
 *
 *  El radio es la mitad del `iconSize: [14, 14]` del DivIcon, menos el borde,
 *  para que los dos puntos se vean del mismo tamaño. */
const VERTICE_STYLE: L.CircleMarkerOptions = {
  radius: 5,
  color: COLOR_AOI,
  weight: 2,
  opacity: 1,
  fillColor: "#ffffff",
  fillOpacity: 1,
};

/** Guía roja que parpadea cuando el polígono que se intenta cerrar se cruza a
 *  sí mismo (reemplaza el `drawError` de leaflet-draw). */
const ERROR_STYLE: L.PathOptions = {
  color: COLOR_ERROR,
  weight: 3,
  opacity: 1,
  fillColor: COLOR_ERROR,
  fillOpacity: 0.2,
};

/** Cuánto dura el parpadeo rojo del aviso de auto-intersección — mismo
 *  `timeout` que tenía el `drawError` de leaflet-draw. */
export const ERROR_TIMEOUT_MS = 2000;

/** Cuánto pueden diferir dos coordenadas para ser el MISMO vértice: una
 *  unidad del redondeo con que se colocan los vértices (7 decimales, ~1 cm).
 *  Se usa para reconocer si un punto cayó sobre un vértice del padre, que es la
 *  condición del autocierre (ver `setVerticesDeAutocierre`). */
const TOLERANCIA_VERTICE_GRADOS = 1e-7;

/** Área mínima, en m², para considerar que dos piezas SOLAPAN de verdad.
 *  Dos polígonos que solo comparten un borde dan una intersección nula o de
 *  área despreciable (ruido de coma flotante en los vértices compartidos);
 *  apoyarse en un límite es válido y no debe bloquear nada. 1 m² es
 *  irrelevante frente a la escala de una finca y deja fuera ese ruido. */
const AREA_SOLAPE_MINIMA_M2 = 1;

/** Largo mínimo, en metros, del pedazo de un tramo que tiene que quedar DENTRO
 *  de otra pieza para considerar que la atraviesa de verdad
 *  (`_segmentoAtraviesa`). Es el equivalente de `AREA_SOLAPE_MINIMA_M2` para un
 *  segmento, que no tiene área con qué medirse, y cumple la misma función:
 *  dibujar pegado al borde de la pieza vecina —lo normal al trazar predios
 *  linderos— hace que el tramo entre y salga del contorno por puro ruido de
 *  coma flotante, y eso NO puede bloquear el dibujo. Un cruce real (entrar y
 *  salir por los brazos de una pieza cóncava) mide muchísimo más que esto. */
const LARGO_INVASION_MINIMA_M = 1;

/* `EDIT_VERTEX_ICON` y `EDIT_MID_VERTEX_ICON` se eliminaron.

   Eran los marcadores del modo Editar (círculo blanco con borde naranja de 12
   px, y el "fantasma" del punto medio de 10 px). Este motor dejó de usarlos
   cuando el paso 3 pasó a `AoiSnapEditEngine`, y se mantuvieron exportados
   porque los importaba el modo Editar del paso 4 — que ponía dos marcadores por
   vértice del lote bajo el cursor.

   Ese motor (`LoteVertexEditEngine`) adoptó la misma metodología del paso 3: UN
   handle al vuelo bajo el cursor, con las clases `.snap-vtx` / `.snap-mid`, y
   los vértices en reposo los dibuja GeoServer. Así que ya no los importa nadie.

   El CSS que usaban (`.leaflet-div-icon.leaflet-editing-icon`) se deja: lo
   siguen usando otros marcadores del wizard. */

/** Indicador "radar" sobre el primer vértice del trazo: pulso animado +
 *  tooltip "Finalizar aquí". Es **decorativo** (`interactive: false` + el
 *  `pointer-events: none` de `.close-indicator-icon`): el click lo recibe el
 *  marcador del vértice, que va encima por `zIndexOffset`. Separarlos es a
 *  propósito — su caja es de 60×60px y, si fuera él quien capturara el click,
 *  se comería clicks del mapa en un radio enorme alrededor del vértice (fue
 *  un bug real). Su CSS (`close-indicator-*`) ya existía en `aoiMap.css`. */
const RADAR_ICON = new L.DivIcon({
  className: "close-indicator-icon",
  html:
    '<div class="close-indicator-wrap">' +
    '<span class="close-indicator-tooltip">Finalizar aquí</span>' +
    '<span class="close-indicator-core"></span>' +
    '<span class="close-indicator-pulse"></span>' +
    '<span class="close-indicator-pulse delay"></span>' +
    "</div>",
  iconSize: [60, 60],
  iconAnchor: [30, 30],
});

/** Pane propio del AOI del usuario: su `<svg>` es un contenedor DEDICADO, no
 *  uno compartido, así participa por sí mismo de la animación de zoom de
 *  Leaflet en vez de quedar congelado en el zoom anterior hasta `zoomend`.
 *
 *  El renderer de este pane lo crea y lo cachea **Leaflet**
 *  (`Map._getPaneRenderer`, que para un pane con nombre siempre elige SVG e
 *  ignora `preferCanvas`). Las capas piden el pane con la opción `pane` y NO
 *  llevan `renderer` propio. Esto importa: retener acá una instancia de
 *  `L.svg({ pane })` creada a mano en el constructor dejaba el polígono sin
 *  pintar (los marcadores de vértice sí, porque son `<div>`). Es el mismo
 *  mecanismo con el que ya se pinta el otro pane con nombre de este mapa.
 *
 *  z-index 450: sobre el `overlayPane` (400) y bajo el `markerPane` (600),
 *  donde viven los vértices — el polígono nunca tapa sus propios marcadores. */
const PANE_AOI = "aoiDibujoPane";
const PANE_AOI_Z_INDEX = "450";

/** `padding` del renderer del AOI. `L.Renderer` solo dibuja dentro de un
 *  rectángulo de viewport + padding, y por defecto ese margen es de apenas
 *  0.1 (10%). Entre `zoomstart` y `zoomend` ese rectángulo NO se recalcula:
 *  al alejarse, todo lo que cae fuera de él queda sin pintar hasta que
 *  termina el zoom, que es justo la sensación de "el polígono se congela y
 *  recién aparece al soltar".
 *
 *  Con 1 el contenedor cubre el viewport completo a cada lado, asi que un
 *  zoom-out normal sigue cayendo adentro y el poligono se mantiene visible
 *  durante toda la animacion. Se puede permitir un margen asi de generoso
 *  porque esta capa tiene MUY pocos paths (los poligonos del AOI), a
 *  diferencia de un renderer compartido con cientos de geometrias.
 *
 *  **NO BAJARLO.** Se probó en 0.5 y volvió el retraso: los vértices se
 *  pintan de una en el zoom nuevo y el polígono llega después. 1 es el valor
 *  que funciona. */
const PANE_AOI_PADDING = 1;

/** Cuánto se puede mover el cursor entre `mousedown` y `mouseup` para que
 *  igual cuente como un click y no como un paneo del mapa.
 *
 *  Este motor NO usa el evento `click` de Leaflet: el navegador solo lo
 *  dispara cuando `mousedown` y `mouseup` resuelven al MISMO elemento, y el
 *  handler de arrastre del mapa corre el pane de tiles bajo el cursor. Con
 *  unos pocos píxeles de temblor de mano al presionar, la bajada caía en un
 *  tile y la subida en otro, el `click` no llegaba al mapa y el vértice
 *  simplemente no se dibujaba — pasaba en 2 o 3 de cada 10 clicks y obligaba a
 *  repetirlos. (`leaflet-draw` esquivaba esto con un marcador invisible pegado
 *  al cursor que capturaba los clicks.)
 *
 *  Resolverlo suspendiendo los handlers del mapa entre bajada y subida
 *  también funcionaría, pero ésa es justo la ventana en la que se panea:
 *  dejaría el mapa inmóvil mientras la herramienta está activa. Con la
 *  tolerancia, un temblor sigue contando como click aunque el mapa se haya
 *  corrido, y un arrastre de verdad sigue paneando normalmente. */
const CLICK_TOLERANCIA_PX = 5;

/** Tolerancia en píxeles para cerrar la forma haciendo click CERCA del primer
 *  vértice, además de encima de él. Es la red de seguridad del cierre: si por
 *  cualquier motivo el click no llega al marcador (algo superpuesto, un dedo
 *  en táctil), igual cierra. Mismo criterio que usaba leaflet-draw. */
const CIERRE_TOLERANCIA_PX = 14;

/** Clase que fuerza el cursor de mano mientras hay una herramienta activa. Va
 *  en el contenedor del mapa y por CLASE, no por estilo inline: Leaflet
 *  alterna sus propias clases de cursor (`leaflet-grab`/`leaflet-dragging`) al
 *  arrastrar, y con un inline el cursor parpadeaba. */
export const CLASE_DIBUJANDO = "aoi-dibujando";

/** Clase que rellena de verde el vértice de cierre cuando un click ahí
 *  TERMINARÍA el polígono. La maneja el motor, no un `:hover` de CSS: ver
 *  `_actualizarResaltadoCierre`. */
const CLASE_CIERRE_ACTIVO = "custom-draw-vertex--cierre-activo";

export type AoiHerramienta = "draw" | "edit" | "delete";

export interface AoiDrawEngineOpciones {
  map: L.Map;
  /** Se acaba de cerrar un polígono nuevo. Recibe su `indice` en el modelo de
   *  piezas (1-based). */
  onDibujado?: (indice: number) => void;
  /** El trazo quedó terminado, y lo que se emite son LOS PUNTOS que el usuario
   *  clickeó — no una pieza.
   *
   *  **Cuando viene, la pieza NO entra al modelo de este motor** y `onDibujado`
   *  no se llama: la geometría final la decide quien escucha. Es lo que usa el
   *  paso 4, donde el lote lo arma el servidor (`DibujarLote`), por el mismo
   *  motivo por el que ya lo arma en el corte, la unión y los vértices — el
   *  navegador no puede ser el que decide qué se guarda.
   *
   *  `cerrado` dice si el usuario volvió al primer vértice (el anillo está
   *  completo) o si el trazo quedó ABIERTO entre dos vértices conocidos, para
   *  que el servidor lo cierre contra el borde que ya tiene guardado — ver
   *  `setVerticesDeAutocierre`.
   *
   *  El paso 3 no lo pasa: ahí la pieza sí vive en el modelo del motor, que es
   *  de donde la lee la unión topológica del AOI. */
  onTrazo?: (puntos: Position[], cerrado: boolean) => void;
  /** El trazo TOCÓ un vértice conocido y por lo tanto es CANDIDATO a
   *  autocerrarse. El motor no lo termina: avisa y sigue dibujando.
   *
   *  **Quién decide es el servidor** (`ValidarAutocierreLote`): acá no hay
   *  forma de saber si los dos extremos caen sobre un mismo contorno ni si lo
   *  que cerrarían pisa otro lote. Antes el motor cerraba por su cuenta en
   *  cuanto veía dos vértices conocidos y emitía el trazo; cuando esa
   *  combinación no cerraba, el usuario perdía todo lo dibujado y recibía un
   *  error. Ahora quien escucha pregunta, confirma con el usuario y, si acepta,
   *  llama a `terminarTrazoAbierto()`.
   *
   *  Recibe el trazo tal como va, con el punto recién puesto incluido. */
  onAutocierrePosible?: (puntos: Position[]) => void;
  /** Cambió la geometría de una pieza ya existente. */
  onEditado?: () => void;
  /** Click sobre una pieza en modo borrar. El motor NO la borra: quien escucha
   *  confirma primero y después llama a `borrar()` (pedido explícito del paso
   *  3, para evitar borrados accidentales). */
  onPedirBorrado?: (indice: number) => void;
  /** Click sobre una pieza en modo EDITAR. La edición la resuelve quien
   *  escucha: lee la geometría por gRPC, la pone en el motor de snap y pinta el
   *  resalte por WMS. Este motor sólo resuelve QUÉ pieza se clickeó. */
  onPedirEdicion?: (indice: number) => void;
  /** Cambió la cantidad de piezas (alta o baja). */
  onCapasCambio?: () => void;
  /** Se rechazó una acción por pisar otra pieza (un vértice adentro, un tramo
   *  que la atraviesa, una forma que la solapa al cerrarse o al editarla). El
   *  motor ya marca en rojo la pieza invadida; esto es para que la UI pueda
   *  además DECIRLO — el rojo solo se entiende si uno ya sabe la regla. */
  onSolapeBloqueado?: () => void;
  /** Pegar SÓLO a los vértices de los bordes, y mostrarlo con un punto igual a
   *  los vértices del trazo (`SNAP_DIBUJO_ICON`) en vez del punto blanco chico.
   *
   *  Lo pide el paso 4: un lote nuevo tiene que apoyarse en los vértices que ya
   *  existen —el mismo punto, no uno cercano— y es además la condición del
   *  autocierre (ver `setVerticesDeAutocierre`). El paso 3 no pasa bordes, así
   *  que para él no cambia nada. */
  snapDeVertices?: boolean;
}

export class AoiDrawEngine {
  constructor(opciones: AoiDrawEngineOpciones) {
    this._opciones = opciones;
    this._map = opciones.map;

    const pane = this._map.getPane(PANE_AOI) ?? this._map.createPane(PANE_AOI);
    pane.style.zIndex = PANE_AOI_Z_INDEX;
    this._ajustarPaddingDelPane();

    // INVARIANTE: TODA capa de este motor (polígonos dibujados, guía del
    // trazo, aviso de cruce) se crea con `pane: PANE_AOI`, y la
    // previsualización de la unión usa el mismo pane (ver `get pane`). Si
    // algún día se agrega una capa nueva acá, tiene que llevar `pane: PANE_AOI`.
    //
    // OJO, acá decía que este pane era "SVG PURO, nunca cae en Canvas": ES
    // FALSO y llevó a diagnosticar mal un problema de rendimiento. Leaflet
    // resuelve el renderer de un pane con `_createRenderer`, que respeta
    // `preferCanvas` del mapa — y los dos mapas del wizard lo tienen en `true`.
    // O sea que estas capas se pintan en CANVAS, igual que los lotes del paso 4.
    //
    // Lo que sí es cierto: ninguna capa lleva `renderer` propio y este motor no
    // retiene una instancia de renderer. Instanciarlo a mano —`L.svg({ pane })`
    // guardado en un campo— dejaba el polígono sin pintar; del renderer del
    // pane se encarga Leaflet. El `CLAUDE.md` además prohíbe el renderer
    // GLOBAL (`map.options.renderer`), que es otra cosa y tampoco se usa.

    this._snap = opciones.snapDeVertices
      ? new MapSnap(this._map, "vertices", SNAP_DIBUJO_ICON)
      : new MapSnap(this._map);

    // **El pane NUNCA captura el puntero.** Antes se iba prendiendo y apagando
    // (`_actualizarCapturaDelPane`) porque las piezas guardadas eran capas
    // interactivas de este pane: el click de Editar y de Borrar lo recibía la
    // capa. Ya no hay ninguna capa interactiva acá —queda el trazo en curso y
    // la pieza recién cerrada, las dos `interactive: false`—, así que el
    // puntero tiene que llegar SIEMPRE al mapa, que es quien resuelve el click
    // contra el modelo de piezas (`_alClickDePieza`).
    //
    // Esto además saca de encima el problema que documentaba ese método: pedir
    // el renderer del pane crea un `<canvas>` del tamaño del viewport a
    // z-index 450, y ese canvas se comía los eventos de las capas de abajo.
    pane.style.pointerEvents = "none";

    // Los eventos de puntero NO se suscriben acá: solo mientras hay una
    // herramienta activa (ver `_engancharEventosDePuntero`).
  }

  /** Bordes contra los que PEGAR los vértices que se dibujan (uno por anillo),
   *  o vacío para dibujar libre.
   *
   *  El paso 3 no pasa ninguno: ahí se traza el perímetro del predio, que no
   *  tiene contra qué apoyarse. El paso 4 pasa los anillos de los lotes, y ahí
   *  el snap no es comodidad sino corrección — un vértice "casi" sobre el borde
   *  del vecino deja una franja de solape que el guardado final rechaza (ver
   *  `MapSnap`).
   *
   *  Hay que volver a llamarlo cuando las geometrías cambian: la guía se arma
   *  con una copia. */
  setBordesDeSnap(bordes: readonly L.LatLng[][]): void {
    this._snap.setBordes(bordes);
  }

  /** Los vértices CONOCIDOS sobre los que el trazo puede autocerrarse.
   *
   *  **Qué habilita.** Si el primer vértice del trazo cae sobre uno de estos y
   *  un click posterior cae sobre OTRO, el trazo termina ahí mismo y se emite
   *  ABIERTO (`onTrazo` con `cerrado: false`): el pedazo de contorno que falta
   *  lo completa el servidor con el borde que ya tiene guardado. Sin esto, el
   *  usuario tiene que clickear vértice por vértice todo el borde del polígono
   *  padre apoyándose en el snap, que son muchos clicks para reponer algo que
   *  la base ya sabe — y cada uno es una oportunidad de dejar una rendija.
   *
   *  Son TODOS los vértices que ya existen —los de los lotes y los del
   *  perímetro—, porque el servidor cierra contra cualquier contorno guardado: el
   *  anillo de un lote, el del perímetro, o el de un hueco encerrado entre
   *  varios. Limitarlos a los del perímetro dejaba afuera los dos casos más
   *  comunes.
   *
   *  Sin `onTrazo` no hace nada: el autocierre emite un trazo abierto, y un
   *  trazo abierto solo lo sabe cerrar el servidor. */
  setVerticesDeAutocierre(vertices: readonly L.LatLng[]): void {
    this._verticesAutocierre = [...vertices];
  }

  /** El modelo de piezas padre. Es GeoJSON plano, NO capas de Leaflet: una
   *  pieza guardada la renderiza sólo el WMS (ver `AoiPiezasState`). De acá
   *  leen el área del badge, el `ConfirmarPiezas` y la unión topológica. */
  get piezas(): AoiPiezasState {
    return this._piezas;
  }

  /** Renderer de los polígonos, para que la previsualización de la unión
   *  comparta pane con ellos y su `bringToFront`/`bringToBack` siga teniendo
   *  sentido (ver `AoiPolygonUnion.setAlFrente`). */
  /** Nombre del pane del AOI, para que la previsualización de la unión se
   *  dibuje en el MISMO `<svg>` y su `bringToFront`/`bringToBack` ordene
   *  contra los polígonos dibujados. */
  get pane(): string {
    return this._pane;
  }

  /** Sube el `padding` del renderer que Leaflet crea para este pane.
   *  `L.Renderer` solo pinta dentro de un rectángulo de viewport + padding, y
   *  por defecto ese margen es de 0.1 (10%). Entre `zoomstart` y `zoomend` ese
   *  rectángulo NO se recalcula: al alejarse, todo lo que cae fuera queda sin
   *  pintar hasta que termina el zoom — que es justo la sensación de que el
   *  polígono se congela y recién aparece al soltar.
   *
   *  `_getPaneRenderer` es interno de Leaflet (no está en @types), pero es la
   *  única vía para llegar al renderer que él mismo crea y cachea por pane.
   *  Se lo llama con el pane ya creado, así que devuelve la instancia SVG. */
  private _ajustarPaddingDelPane(): void {
    const mapa = this._map as unknown as {
      _getPaneRenderer(name: string): L.Renderer | false;
    };
    const renderer = mapa._getPaneRenderer(PANE_AOI);
    if (renderer) renderer.options.padding = PANE_AOI_PADDING;
  }

  get herramienta(): AoiHerramienta | null {
    return this._herramienta;
  }

  /** Activa una herramienta; si ya estaba activa, la desactiva (toggle).
   *  Devuelve la que quedó activa, o `null`. */
  activar(id: AoiHerramienta): AoiHerramienta | null {
    this._cancelarTrazo();

    if (this._herramienta === id) {
      this.desactivar();
      return null;
    }

    this._herramienta = id;
    this._marcarDibujando(true);

    // Los eventos van para las TRES herramientas, no sólo para Dibujar. Antes
    // Editar y Borrar no escuchaban nada del mapa porque el click lo recibía
    // la capa de la pieza; sin esas capas, el click del mapa es el único
    // camino y hay que resolverlo con la misma tolerancia que el dibujo (ver
    // `CLICK_TOLERANCIA_PX`: el `click` de Leaflet se pierde cuando la bajada
    // y la subida caen en tiles distintos).
    this._engancharEventosDePuntero();

    if (id === "draw") {
      // Un doble click al terminar un trazo es un reflejo común: sin esto,
      // además de agregar vértices haría zoom. Solo aplica DIBUJANDO — antes
      // se desactivaba para cualquier herramienta, así que en Editar el mapa
      // dejaba de responder al doble click sin ninguna razón.
      this._map.doubleClickZoom.disable();
    } else {
      this._map.doubleClickZoom.enable();
    }

    return this._herramienta;
  }

  desactivar(): void {
    this._snap.ocultar();
    this._cancelarTrazo();
    this._soltarEventosDePuntero();
    this._herramienta = null;
    this._marcarDibujando(false);
    this._map.doubleClickZoom.enable();
  }

  /** Engancha los eventos de puntero del mapa. Se llama al activar cualquier
   *  herramienta; sin ninguna activa el motor no escucha NADA, así no hay
   *  manera de que sus handlers se crucen con el zoom o el arrastre. */
  private _engancharEventosDePuntero(): void {
    if (this._eventosPunteroEnganchados) return;
    this._map.on("mousedown", this._alMouseDown);
    this._map.on("mouseup", this._alMouseUp);
    this._map.on("mousemove", this._alMover);
    this._map.on("mouseout", this._alSalirDelMapa);
    this._map.on("zoomstart", this._alZoomStart);
    this._map.on("zoomend", this._alZoomEnd);
    this._eventosPunteroEnganchados = true;
  }

  private _soltarEventosDePuntero(): void {
    if (!this._eventosPunteroEnganchados) return;
    this._map.off("mousedown", this._alMouseDown);
    this._map.off("mouseup", this._alMouseUp);
    this._map.off("mousemove", this._alMover);
    this._map.off("mouseout", this._alSalirDelMapa);
    this._map.off("zoomstart", this._alZoomStart);
    this._map.off("zoomend", this._alZoomEnd);
    this._eventosPunteroEnganchados = false;
    this._puntoBajada = null;
    this._zoomEnCurso = false;
    this._cursorEnPantalla = null;
  }

  /** Quita una pieza del modelo (después de confirmar el borrado). */
  borrar(indice: number): void {
    this._piezas.quitar(indice);
    this._notificarCambio();
  }

  /** Carga piezas que ya existen: las restauradas del borrador, las
   *  reconstruidas de los lotes, o un KML importado en el paso 2.
   *
   *  @param enLaBase `true` si estas piezas ya están en `poligonos_padre`, o
   *   sea si el WMS las va a pintar. Es el caso de las restauradas del
   *   borrador (`leido.poligonosPadre`).
   *
   *   `false` cuando NO están guardadas todavía: un KML recién importado, o las
   *   piezas reconstruidas de los lotes (`fx_piezasDesdeLotes`), que se arman
   *   en el cliente y no existen en esa columna. Ésas sí las pinta Leaflet,
   *   porque si no el usuario vería el mapa en blanco hasta el primer
   *   guardado. Es el mismo criterio que una pieza recién dibujada, y sostiene
   *   el invariante del paso: **lo que Leaflet pinta es exactamente lo que
   *   todavía no está en la base.**
   *
   *   No se dispara un guardado acá a propósito. Para el KML sería inofensivo,
   *   pero para las piezas reconstruidas de los lotes no: guardar un perímetro
   *   que difiere del `aoi` guardado aunque sea por el redondeo hace que el
   *   servidor lo tome como un cambio de área y BORRE los lotes en los que la
   *   finca ya estaba subdividida. El guardado llega cuando el usuario toca
   *   algo, o al pulsar "Continuar", que es como venía funcionando. */
  /** @param ids El id de cada pieza en `tbl_temp_poligonos_padre`, en la misma
   *   posición. Sólo lo traen las piezas restauradas del borrador: un KML
   *   recién importado y las piezas reconstruidas de los lotes no están
   *   guardadas, así que no tienen id hasta el primer `GuardarAoi`. Ver
   *   `AoiPieza.id`. */
  cargarExistentes(
    polygons: Polygon[],
    enLaBase: boolean,
    ids: readonly string[] = [],
  ): void {
    // Las piezas restauradas (borrador, KML) pasan por el mismo redondeo que
    // las dibujadas a mano: si no, convivirían dos precisiones en el mismo
    // mapa y volverían los bordes que no cierran.
    const truncados = polygons.map((poligono) => fx_truncarPolygon(poligono));
    this._piezas.reemplazar(truncados, false, ids);
    if (!enLaBase) {
      truncados.forEach((polygon) => this._pintarEnCreacion(polygon));
    }
    this._notificarCambio();
  }

  /** Reescribe la geometría de una pieza: la editó el usuario con los
   *  vértices. Queda marcada como modificada. */
  escribirPieza(indice: number, polygon: Polygon): void {
    this._piezas.escribir(indice, polygon);
    this._opciones.onEditado?.();
  }

  /** Devuelve una pieza a una geometría anterior, con su marca de modificada
   *  tal como estaba. Lo usa la reversa de una edición que el servidor rechazó
   *  (`AoiDrawToolbar.revertirEdicionRechazada`).
   *
   *  No dispara `onEditado`: quien revierte decide si hace falta avisar, porque
   *  avisar encola otro guardado. */
  restaurarPieza(indice: number, polygon: Polygon, modificada: boolean): void {
    this._piezas.escribir(indice, polygon, modificada);
  }

  /** **Las piezas ya están en la base**: se descartan las capas de Leaflet que
   *  las venían pintando, porque de acá en más las pinta el WMS.
   *
   *  Lo llama el paso 3 cuando el `GuardarAoi` volvió bien y ya pidió el
   *  refresco de los tiles. El orden importa: si las capas se sacaran al cerrar
   *  el polígono, entre el cierre y la vuelta del guardado el mapa quedaría un
   *  momento sin nada dibujado.
   *
   *  Si el guardado FALLA no se llama, y las capas siguen en pantalla — que es
   *  lo correcto: esa pieza no está en la base, así que el WMS no la pinta y
   *  Leaflet es lo único que la muestra. */
  confirmarPersistencia(): void {
    for (const capa of this._capasEnCreacion) this._map.removeLayer(capa);
    this._capasEnCreacion = [];
  }

  /** Pinta en Leaflet una pieza que todavía NO está en la base. Es el único
   *  caso en que este motor dibuja una forma cerrada: mientras se está
   *  creando. `interactive: false` — el click lo resuelve el mapa contra el
   *  modelo, no la capa. */
  private _pintarEnCreacion(polygon: Polygon): void {
    const anillos = polygon.coordinates.map((anillo) =>
      anillo.map((coordenada) => L.latLng(coordenada[1], coordenada[0])),
    );
    const capa = L.polygon(anillos, {
      ...POLYGON_STYLE,
      pane: PANE_AOI,
      interactive: false,
    }).addTo(this._map);
    this._capasEnCreacion.push(capa);
  }

  /** Avisa que el modelo de piezas cambió (alta, baja o reemplazo). Antes esto
   *  salía del `layeradd`/`layerremove` del `FeatureGroup`; sin capas hay que
   *  decirlo a mano. */
  private _notificarCambio(): void {
    this._opciones.onCapasCambio?.();
  }

  /** Libera listeners y capas. Llamar al desmontar el paso. */
  destruir(): void {
    this._snap.dispose();
    this._soltarEventosDePuntero();
    this._cancelarTrazo();
    this.confirmarPersistencia();
    this._piezas.vaciar();
    this._marcarDibujando(false);
    this._map.doubleClickZoom.enable();
  }

  private readonly _opciones: AoiDrawEngineOpciones;
  private readonly _map: L.Map;
  private readonly _snap: MapSnap;
  /** Las piezas padre, en GeoJSON y fuera de Leaflet. */
  private readonly _piezas = new AoiPiezasState();
  /** Las capas de las piezas que todavía NO están en la base. Se vacía en
   *  `confirmarPersistencia`. */
  private _capasEnCreacion: L.Polygon[] = [];
  private readonly _pane = PANE_AOI;

  private _herramienta: AoiHerramienta | null = null;

  /** Estado del trazo en curso. */
  /** Si los eventos de puntero están enganchados al mapa. Evita suscribir o
   *  quitar dos veces al alternar herramientas. */
  private _eventosPunteroEnganchados = false;
  /** Punto de pantalla donde bajó el botón del mouse, para distinguir un
   *  click de un paneo en `_alMouseUp`. */
  private _puntoBajada: L.Point | null = null;
  private _vertices: L.LatLng[] = [];
  /** Vértices del polígono padre contra los que se puede autocerrar (ver
   *  `setVerticesDeAutocierre`). */
  private _verticesAutocierre: L.LatLng[] = [];
  /** ¿El PRIMER vértice del trazo cayó sobre un vértice conocido? Es la mitad
   *  de la condición del autocierre, y se contesta al colocarlo: después el
   *  trazo ya avanzó y no hay cómo volver a preguntarlo. */
  private _primeroEsConocido = false;
  private _marcadores: L.Marker[] = [];
  /** Los vértices ya puestos del trazo, **en su propia capa**: un círculo por
   *  click, aparte de la guía que une los puntos (`_previa`).
   *
   *  Son dos capas y no una porque son dos cosas: la guía cambia con cada
   *  movimiento del cursor (su último tramo cuelga del mouse) y los vértices
   *  sólo con cada click. Dibujarlos juntos obligaría a rehacer los puntos en
   *  cada `mousemove`, que es justo lo que no hace falta. */
  private _verticesCapa: L.LayerGroup | null = null;
  private _previa: L.Path | null = null;
  /** Si `_previa` es hoy un polígono (3+ puntos) o una polilínea. Decide si
   *  alcanza con `setLatLngs` o hay que recrear la capa. */
  private _previaEsPoligono = false;
  /** Último pixel del contenedor donde estuvo el cursor, para repintar la
   *  guía al terminar un zoom. */
  private _cursorEnPantalla: L.Point | null = null;
  private _zoomEnCurso = false;
  private _radar: L.Marker | null = null;
  private _avisoCruce: L.Polygon | null = null;
  private _avisoCruceTimer: ReturnType<typeof setTimeout> | null = null;

  // --- Eventos del mapa ----------------------------------------------------

  private _alMouseDown = (e: L.LeafletMouseEvent): void => {
    if (this._herramienta === null) return;
    this._puntoBajada = e.containerPoint;
  };

  /** Decide si lo que acaba de pasar fue un click o un paneo. Se mide contra
   *  el punto donde bajó el botón, no contra la coordenada geográfica: si el
   *  mapa se corrió durante la pulsación, el `latlng` cambia aunque el cursor
   *  no se haya movido en pantalla. */
  private _alMouseUp = (e: L.LeafletMouseEvent): void => {
    if (this._herramienta === null) return;

    const bajada = this._puntoBajada;
    this._puntoBajada = null;
    // Sin bajada registrada la pulsación empezó sobre algo que no propaga sus
    // eventos al mapa (un marcador de vértice): no es un click del mapa.
    if (!bajada) return;
    if (bajada.distanceTo(e.containerPoint) > CLICK_TOLERANCIA_PX) return;

    // Editar y Borrar trabajan sobre una pieza que YA existe: hay que resolver
    // cuál, porque las piezas no son capas y no hay un `layer.on("click")` que
    // lo diga.
    if (this._herramienta !== "draw") {
      this._alClickDePieza(e.latlng);
      return;
    }

    // Todo lo que sigue trabaja sobre el punto PEGADO, no sobre donde está el
    // cursor: es el que se va a guardar, así que también es contra el que hay
    // que validar. Sin bordes de snap devuelve el mismo punto y nada cambia.
    const latlng = this._snap.aplicar(e.latlng);

    // Red de seguridad del cierre: un click CERCA del primer vértice también
    // cierra, no solo uno exactamente encima de su marcador. Sin esto, si algo
    // se superpone al marcador el trazo no se puede terminar de ninguna forma
    // — que es lo que pasó con el radar comiéndose el click.
    if (this._vertices.length >= 3 && this._cercaDelPrimerVertice(latlng)) {
      this._cerrarPoligono();
      return;
    }

    // Autocierre: se arrancó sobre un vértice conocido y este click cae sobre
    // otro. Va DESPUÉS del cierre manual a propósito — si el click es sobre el
    // primer vértice, lo que corresponde es cerrar el anillo, no autocerrar.
    if (this._puedeAutocerrarEn(latlng)) {
      this._autocerrar(latlng);
      return;
    }

    // No se permite que el trazo pise otra pieza ya dibujada: dos piezas
    // solapadas rompen la unión topológica del AOI. Se mira el vértice, el
    // TRAMO que se está por fijar y la forma provisional — ver
    // `_invadeOtraPieza`.
    if (this._invadeOtraPieza(latlng) !== null) {
      this._avisarSolape();
      return;
    }

    // Ni uno que haga que el trazo se cruce a sí mismo. Se valida ACA, al
    // colocar cada vértice, y no solo al cerrar: antes se podía dibujar una
    // forma en X sin ningún aviso y el rechazo llegaba recién al intentar
    // cerrarla, lo que desde afuera se veía como "el click sobre el primer
    // vértice no funciona".
    if (this._cruzaElTrazo(latlng)) {
      this._avisarCruce();
      return;
    }

    this._agregarVertice(latlng);
  };

  /** La pieza que CONTIENE a este punto, o `null`. La resuelve el modelo
   *  (`AoiPiezasState.contieneElPunto`), que es donde viven las geometrías.
   *
   *  El paso 3 arma el AOI como piezas que después se unen topológicamente
   *  (`AoiPolygonUnion`). Dos piezas solapadas rompen esa unión, así que no se
   *  permite poner un vértice dentro de otra pieza — ni dibujando ni editando.
   *  `excluir` deja fuera a la pieza que se está editando: sus propios
   *  vértices están, por definición, sobre su borde. */
  private _piezaQueContiene(latlng: L.LatLng, excluir?: number): number | null {
    return this._piezas.contieneElPunto(latlng.lng, latlng.lat, excluir);
  }

  /** ¿El vértice candidato deja el trazo pisando una pieza ya dibujada?
   *  Devuelve la pieza invadida (para marcarla en rojo), o `null`.
   *
   *  Solo se miran cosas DEFINITIVAS: las que al hacer click quedan fijas y no
   *  pueden volverse válidas más adelante.
   *   1. el vértice cae dentro de otra pieza (`_piezaQueContiene`);
   *   2. el TRAMO que se está por fijar la atraviesa, aunque sus dos extremos
   *      queden afuera (`_segmentoAtraviesa`).
   *
   *  **NO se valida la forma provisional** —el anillo que saldría de cerrar el
   *  trazo AHORA— ni con el área ni de ninguna otra manera. Esa forma es un
   *  artefacto del dibujo a medio hacer, no el polígono que el usuario está
   *  trazando: rodear una pieza vecina, o simplemente traerle el cursor cerca,
   *  pasa por estados intermedios que la pisan y que dejan de pisarla en
   *  cuanto se agrega el vértice siguiente. Validar ahí bloquea trazos
   *  perfectamente válidos. El chequeo por área es de `_cerrarPoligono`, donde
   *  la forma ya es definitiva. */
  private _invadeOtraPieza(destino: L.LatLng): number | null {
    const contiene = this._piezaQueContiene(destino);
    if (contiene !== null) return contiene;

    return this._segmentoAtraviesa(destino);
  }

  /** ¿El tramo que iría del último vértice a `destino` ATRAVIESA alguna pieza
   *  ya dibujada? Devuelve la primera que encuentra, o `null`.
   *
   *  Es el agujero que dejaban los otros dos chequeos: `_piezaQueContiene`
   *  mira VÉRTICES, no tramos, y `_solapaConOtraPieza` mide ÁREA, que hasta
   *  el 3er vértice todavía no existe. Con una pieza cóncava —una "V", una
   *  ">"— se pueden poner dos vértices consecutivos FUERA de ella y que el
   *  tramo que los une le cruce los dos brazos: nada lo detectaba mientras se
   *  dibujaba y el rechazo llegaba recién al cerrar, con el polígono entero ya
   *  trazado.
   *
   *  Cómo: se parte el tramo por los puntos donde toca el contorno de la pieza,
   *  se mide cuánto de él queda por DENTRO (punto medio de cada pedazo, con
   *  `ignoreBoundary: true`) y se bloquea solo si eso supera
   *  `LARGO_INVASION_MINIMA_M`. La tolerancia no es un detalle: dibujar pegado
   *  al borde de la pieza vecina es lo normal al trazar predios linderos, y ahí
   *  el tramo entra y sale del contorno por puro ruido de coma flotante —
   *  bloquear con eso haría imposible dibujar al lado de otra pieza. Mismo
   *  criterio que `AREA_SOLAPE_MINIMA_M2` en el chequeo por área.
   *
   *  Este SÍ es un dato definitivo, a diferencia de la forma provisional (ver
   *  `_invadeOtraPieza`): el tramo que fija este click es una arista del
   *  polígono final, y una arista que atraviesa la pieza vecina la va a solapar
   *  sí o sí, se dibuje lo que se dibuje después. */
  private _segmentoAtraviesa(destino: L.LatLng): number | null {
    const origen = this._vertices[this._vertices.length - 1];
    if (!origen) return null;

    const ax = origen.lng;
    const ay = origen.lat;
    const dx = destino.lng - ax;
    const dy = destino.lat - ay;
    const largo2 = dx * dx + dy * dy;
    // Dos clicks en el mismo punto: no hay tramo que evaluar.
    if (largo2 === 0) return null;

    const largoMetros = turf.distance(
      turf.point([ax, ay]),
      turf.point([destino.lng, destino.lat]),
      { units: "meters" },
    );
    // Tramo más corto que la propia tolerancia: no puede atravesar nada.
    if (largoMetros <= LARGO_INVASION_MINIMA_M) return null;

    const tramo = turf.lineString([
      [ax, ay],
      [destino.lng, destino.lat],
    ]);
    const minLng = Math.min(ax, destino.lng);
    const maxLng = Math.max(ax, destino.lng);
    const minLat = Math.min(ay, destino.lat);
    const maxLat = Math.max(ay, destino.lat);

    for (const { indice, feature: existente, bbox } of this._piezas.recorrer()) {
      // Descarte barato ANTES de tocar turf: sin solape de cajas no hay nada
      // que calcular. Importa porque esto corre también en cada `mousemove`
      // para pintar la guía. La caja de la pieza la cachea el modelo.
      if (
        maxLng < bbox[0] || minLng > bbox[2] ||
        maxLat < bbox[1] || minLat > bbox[3]
      ) continue;

      try {
        // Parámetro (0..1) de cada punto donde el tramo toca el contorno. Como
        // el tramo es recto en lng/lat —así lo trata turf y así se guarda
        // después—, proyectar sobre él es exacto.
        const cortes = turf
          .lineIntersect(tramo, existente)
          .features.map((f) => {
            const [x, y] = f.geometry.coordinates;
            return ((x - ax) * dx + (y - ay) * dy) / largo2;
          })
          .filter((t) => t > 0 && t < 1);

        // Se SUMAN todos los pedazos interiores: una pieza cóncava puede partir
        // el tramo en varios, y ninguno por separado tiene por qué llegar a la
        // tolerancia aunque juntos sean una invasión clara.
        const marcas = [0, ...cortes, 1].sort((p, q) => p - q);
        let adentroMetros = 0;
        for (let i = 0; i < marcas.length - 1; i++) {
          const medio = (marcas[i] + marcas[i + 1]) / 2;
          const punto = turf.point([ax + dx * medio, ay + dy * medio]);
          if (
            turf.booleanPointInPolygon(punto, existente, {
              ignoreBoundary: true,
            })
          ) {
            adentroMetros += (marcas[i + 1] - marcas[i]) * largoMetros;
          }
        }
        if (adentroMetros > LARGO_INVASION_MINIMA_M) return indice;
      } catch {
        // Geometría inválida: no puede bloquear el dibujo.
      }
    }
    return null;
  }

  /** Avisa que se rechazó algo por pisar otra pieza.
   *
   *  **Es el embudo de TODOS los rechazos por solape** —vértice adentro, tramo
   *  que atraviesa, forma que solapa al cerrar—, así que un rechazo nuevo que
   *  pase por acá queda avisado solo.
   *
   *  **Ya no se pinta de rojo la pieza invadida.** Era un `setStyle` sobre su
   *  capa, y una pieza guardada no tiene capa: la pinta el WMS. Resaltarla
   *  ahora sería un GetMap por cada click rechazado, y rechazar es algo que
   *  pasa seguido mientras se dibuja pegado a un vecino. Queda el aviso de
   *  texto, que es el que de verdad explica la regla — el rojo solo se
   *  entendía sabiéndola de antes. El parpadeo rojo del TRAZO (`_avisarCruce`)
   *  no se tocó: ése es la forma en curso, que sí es de este motor. */
  private _avisarSolape(): void {
    this._opciones.onSolapeBloqueado?.();
  }

  /** ¿La forma que se va a cerrar SOLAPA en área con alguna pieza ya
   *  dibujada? Devuelve la primera que encuentra, o `null`.
   *
   *  Bloquear vértices interiores (`_dentroDeOtroPoligono`) atrapa el caso
   *  común, pero no alcanza: dos polígonos pueden cruzarse por las ARISTAS
   *  sin que ningún vértice caiga dentro del otro, y ese solape rompe igual
   *  la unión topológica. Acá se mide la intersección real con
   *  `turf.intersect` y se compara su área contra `AREA_SOLAPE_MINIMA_M2`,
   *  para que compartir un borde siga siendo válido. */
  private _solapaConOtraPieza(
    vertices: L.LatLng[],
    excluir?: number,
  ): number | null {
    const anillo: Position[] = vertices.map((v) => [v.lng, v.lat]);
    anillo.push(anillo[0]);
    return this._piezas.solapa(anillo, AREA_SOLAPE_MINIMA_M2, excluir);
  }

  private _redondear(latlng: L.LatLng): L.LatLng {
    return L.latLng(
      fx_redondearCoordenada(latlng.lat),
      fx_redondearCoordenada(latlng.lng),
    );
  }

  private _cercaDelPrimerVertice(latlng: L.LatLng): boolean {
    const primero = this._vertices[0];
    if (!primero) return false;
    return (
      this._map
        .latLngToContainerPoint(primero)
        .distanceTo(this._map.latLngToContainerPoint(latlng)) <=
      CIERRE_TOLERANCIA_PX
    );
  }

  private _alMover = (e: L.LeafletMouseEvent): void => {
    if (this._herramienta !== "draw") return;
    // Se guarda el punto de PANTALLA, no el `latlng`: al terminar un zoom la
    // coordenada geográfica bajo el cursor es otra, pero el pixel es el mismo.
    this._cursorEnPantalla = e.containerPoint;

    // El indicador de snap se muestra desde ANTES del primer vértice: avisa
    // que el click no va a caer donde está el cursor, y eso vale también para
    // el punto de arranque.
    const pegado = this._snap.buscar(e.latlng);
    this._snap.mostrar(pegado);
    const latlng = pegado ?? e.latlng;

    if (this._vertices.length === 0) return;
    // Durante la animación de zoom la guía no se toca (ver `_alZoomStart`).
    if (this._zoomEnCurso) return;
    this._actualizarResaltadoCierre(latlng);
    // La guía se pinta roja cuando soltar ahí cruzaría el trazo O pisaría otra
    // pieza, así el usuario ve el impedimento ANTES de hacer click. De las
    // tres comprobaciones de `_invadeOtraPieza` acá corren las dos baratas
    // (el vértice y el tramo); la de área se deja para el click, que es donde
    // el rechazo tiene que ser exacto — esto corre en cada `mousemove`.
    const invalida =
      this._cruzaElTrazo(latlng) ||
      this._piezaQueContiene(latlng) !== null ||
      this._segmentoAtraviesa(latlng) !== null;
    this._repintarPrevia(latlng, invalida);
  };

  /** Mientras dura el zoom, la guía del trazo queda congelada: repintarla ahí
   *  la proyectaría a un zoom INTERMEDIO mientras el renderer está a medio
   *  `transform`, y las dos actualizaciones se pelean el mismo frame. Con
   *  muchos vértices eso trababa el zoom entero. */
  private _alZoomStart = (): void => {
    this._zoomEnCurso = true;
  };

  /** Al terminar el zoom se repinta UNA vez, reproyectando desde el pixel
   *  donde quedó el cursor. */
  private _alZoomEnd = (): void => {
    this._zoomEnCurso = false;
    if (!this._cursorEnPantalla || this._vertices.length === 0) return;
    const cursor = this._map.containerPointToLatLng(this._cursorEnPantalla);
    // La tolerancia de cierre es en PÍXELES: con otro zoom el cursor puede
    // haber quedado dentro o fuera de ella sin moverse.
    this._actualizarResaltadoCierre(cursor);
    this._repintarPrevia(cursor);
  };

  private _alSalirDelMapa = (): void => {
    this._actualizarResaltadoCierre(null);
  };

  /** Pinta de verde el vértice de cierre SOLO cuando un click en la posición
   *  del cursor cerraría de verdad el polígono: 3+ vértices y cursor dentro de
   *  `CIERRE_TOLERANCIA_PX` del primero — exactamente la condición de
   *  `_alMouseUp`, así el verde nunca promete un cierre que no va a pasar.
   *
   *  Reemplaza al `:hover` de CSS que había antes: el marcador nace debajo del
   *  cursor (se crea en el `mouseup` del mismo click), y el navegador le
   *  aplicaba `:hover` en el acto — el primer punto de cada polígono se veía
   *  verde al crearse y volvía a blanco al mover el mouse. */
  private _actualizarResaltadoCierre(cursor: L.LatLng | null): void {
    const elemento = this._marcadores[0]?.getElement();
    if (!elemento) return;
    const activo =
      cursor !== null &&
      this._vertices.length >= 3 &&
      this._cercaDelPrimerVertice(cursor);
    elemento.classList.toggle(CLASE_CIERRE_ACTIVO, activo);
  }

  // --- Trazo ---------------------------------------------------------------

  /** Agrega un vértice al trazo.
   *
   *  **Sólo el PRIMER vértice lleva marcador**, porque es el único que hace
   *  algo: es el objetivo de cierre (lleva el radar y su click termina la
   *  forma). Los intermedios eran decorativos y se ven igual en la guía que
   *  dibuja `_repintarPrevia`.
   *
   *  Antes se creaba un `L.marker` por click. Cada uno es un nodo del DOM con
   *  su listener, que Leaflet reposiciona en cada zoom, pan y movimiento del
   *  cursor: dibujando un perímetro de decenas de vértices el trazo se volvía
   *  pesado, y cada click nuevo costaba más que el anterior. Ahora el costo por
   *  click no crece.
   *
   *  El cierre no depende de este marcador: `_alMouseUp` cierra por proximidad
   *  al primer vértice (`CIERRE_TOLERANCIA_PX`), así que el click sobre el
   *  marcador es un atajo, no el único camino. */
  private _agregarVertice(latlng: L.LatLng): void {
    // Se redondea AL COLOCARLO, no al guardar: así lo que se ve dibujado es
    // exactamente lo que después se valida, se une y se corta. Ver
    // `PRECISION_COORDENADAS`.
    this._vertices.push(this._redondear(latlng));
    const esCierre = this._vertices.length === 1;
    if (esCierre) {
      this._primeroEsConocido = this._esVerticeConocido(
        this._vertices[0],
      );
    }

    // El vértice NO viaja al servidor (2026-10-23). El trazo en curso vive sólo
    // acá, en memoria, y lo dibuja Leaflet (`_repintarPrevia`, al final). Antes
    // cada click era un `AgregarVerticeTrazo` por gRPC y el punto no aparecía
    // hasta que GeoServer devolvía el tile.

    // El punto del vértice, en su capa aparte. El primero NO lleva círculo:
    // tiene su propio marcador (el de cierre, amarillo y con el radar), y los
    // dos superpuestos se verían como un punto sucio.
    if (!esCierre) this._pintarVertice(this._vertices[this._vertices.length - 1]);

    if (esCierre) {
      const marcador = L.marker(latlng, {
        icon: VERTICE_CIERRE_ICON,
        keyboard: false,
        // Por encima del radar (`zIndexOffset: -1000`), que comparte
        // `markerPane` y está justo en esta misma posición.
        zIndexOffset: 1000,
      });

      marcador.on("click", (ev: L.LeafletMouseEvent) => {
        // Sin esto el click también llega al mapa y agrega otro vértice encima
        // del que ya está.
        L.DomEvent.stop(ev);
        this._cerrarPoligono();
      });

      marcador.addTo(this._map);
      this._marcadores.push(marcador);
      this._mostrarRadar(latlng);
    }

    this._repintarPrevia(latlng);
  }

  /** Agrega el círculo de un vértice a la capa de vértices, creándola si es el
   *  primero. `interactive: false` por lo mismo que la guía: si capturara el
   *  click, un vértice puesto encima del de cierre impediría cerrar la forma. */
  private _pintarVertice(latlng: L.LatLng): void {
    if (!this._verticesCapa) {
      this._verticesCapa = L.layerGroup([], { pane: PANE_AOI }).addTo(this._map);
    }
    this._verticesCapa.addLayer(
      L.circleMarker(latlng, {
        ...VERTICE_STYLE,
        pane: PANE_AOI,
        interactive: false,
      }),
    );
  }

  /** Saca la capa de vértices del mapa. */
  private _quitarVertices(): void {
    if (!this._verticesCapa) return;
    this._map.removeLayer(this._verticesCapa);
    this._verticesCapa = null;
  }

  /** Redibuja la guía del trazo: vértices fijos + la posición del cursor.
   *  `interactive: false` es CLAVE — si la guía captura el click, nunca llega
   *  al marcador del primer vértice y la forma no se puede cerrar. */
  private _repintarPrevia(cursor: L.LatLng, invalida = false): void {
    if (this._vertices.length === 0) return;
    const puntos = [...this._vertices, cursor];
    const debeSerPoligono = puntos.length >= 3;
    const estilo = invalida ? ERROR_STYLE : POLYGON_STYLE;

    // Reusar la capa, NO recrearla. Antes se hacía `removeLayer` + `L.polygon`
    // nuevo en CADA `mousemove`: con muchos vértices eso reproyecta los N+1
    // puntos, reinicializa el `<path>` en el renderer y dispara
    // `layeradd`/`layerremove` en el mapa, todo por cada pixel que se mueve el
    // cursor. `setLatLngs` hace solo la reproyección, que es lo único que de
    // verdad cambió.
    if (this._previa && this._previaEsPoligono === debeSerPoligono) {
      (this._previa as L.Polyline).setLatLngs(puntos);
      this._previa.setStyle(estilo);
      return;
    }

    // Solo se recrea al cambiar de tipo: polilínea hasta el 2º vértice,
    // polígono del 3º en adelante. Pasa UNA vez por trazo.
    if (this._previa) this._map.removeLayer(this._previa);
    const opciones = { pane: PANE_AOI, interactive: false };
    const guia: L.Path = debeSerPoligono
      ? L.polygon(puntos, { ...estilo, ...opciones })
      : L.polyline(puntos, { ...estilo, ...opciones });
    guia.addTo(this._map);
    this._previa = guia;
    this._previaEsPoligono = debeSerPoligono;
  }

  /* `_quitarPrevia` se eliminó (2026-10-23): su único llamador era el corto
     circuito que apagaba la guía local cuando el trazo lo renderizaba
     GeoServer. La guía ya no se apaga nunca mientras hay trazo, y al terminarlo
     la saca `_cancelarTrazo`. */

  /** Cierra el polígono en curso si es válido. La herramienta queda activa
   *  para encadenar el siguiente (comportamiento de la referencia). */
  private _cerrarPoligono(): void {
    if (this._vertices.length < 3) return;
    if (this._seCruza(this._vertices)) {
      this._avisarCruce();
      return;
    }

    // Chequeo de solape REAL contra las piezas ya dibujadas, medido por área.
    // Es el cierre riguroso del caso que `_dentroDeOtroPoligono` no cubre: dos
    // formas que se cruzan por las aristas sin vértices interiores. El trazo
    // NO se cancela — queda en curso para que el usuario lo corrija.
    if (this._solapaConOtraPieza(this._vertices) !== null) {
      this._avisarSolape();
      this._avisarCruce();
      return;
    }

    // La pieza entra al MODELO, no al mapa como una capa más. Lo único que se
    // dibuja en Leaflet es la capa de creación, que vive hasta que el guardado
    // vuelve y el WMS la incluye (ver `confirmarPersistencia`).
    const polygon: Polygon = {
      type: "Polygon",
      coordinates: [
        [
          ...this._vertices.map((v) => [v.lng, v.lat] as Position),
          [this._vertices[0].lng, this._vertices[0].lat] as Position,
        ],
      ],
    };
    // Con `onTrazo`, la pieza NO entra al modelo de este motor: lo que se emite
    // son los puntos y la geometría la decide quien escucha (el servidor, en el
    // paso 4). Tampoco se pinta la capa "en creación": el lote que vuelva lo
    // dibuja el WMS, y pintarlo acá sería renderizarlo dos veces.
    if (this._opciones.onTrazo) {
      const puntos = polygon.coordinates[0];
      this._cancelarTrazo();
      this._opciones.onTrazo(puntos, true);
      return;
    }

    const indice = this._piezas.agregar(polygon);
    this._pintarEnCreacion(polygon);
    this._cancelarTrazo();
    this._notificarCambio();
    this._opciones.onDibujado?.(indice);
  }

  /** ¿Un click acá termina el trazo contra el borde del padre?
   *
   *  Las tres condiciones: que haya quien reciba un trazo abierto, que el
   *  primer vértice haya caído sobre un vértice conocido, y que este punto sea
   *  otro vértice conocido. "Otro" se mide contra el primero: volver al mismo
   *  no autocierra nada (eso es el cierre manual, y lo atiende la rama de
   *  arriba). */
  private _puedeAutocerrarEn(latlng: L.LatLng): boolean {
    if (!this._opciones.onTrazo) return false;
    if (!this._primeroEsConocido || this._vertices.length === 0) return false;
    if (this._mismoVertice(this._vertices[0], latlng)) return false;
    return this._esVerticeConocido(latlng);
  }

  /** Pone el vértice y AVISA que el trazo podría autocerrarse. No lo termina.
   *
   *  **El motor ya no cierra por su cuenta.** Lo hacía: en cuanto el trazo
   *  tocaba un segundo vértice conocido lo emitía abierto y el servidor
   *  intentaba cerrarlo. Si esos dos vértices no estaban sobre un mismo
   *  contorno —dos lotes que no son vecinos, por ejemplo— o lo que cerraban
   *  pisaba otro lote, el trazo entero se perdía y salía un error. Dos clicks
   *  desafortunados, no un error del usuario.
   *
   *  Ahora quien escucha le pregunta al servidor y, si se puede, lo confirma
   *  con el usuario; recién ahí llama a `terminarTrazoAbierto()`. Si no se
   *  puede, no pasa nada: el vértice queda puesto y se sigue dibujando. */
  private _autocerrar(latlng: L.LatLng): void {
    // El punto se agrega como cualquier otro: así queda en la base con el
    // resto del trazo y el usuario lo ve dibujado, igual que los anteriores.
    this._agregarVertice(latlng);
    this._opciones.onAutocierrePosible?.(this._puntosDelTrazo());
  }

  /** Cierra el trazo en curso y lo emite ABIERTO, para que el servidor lo
   *  complete con el borde existente. Lo llama quien confirmó el autocierre
   *  con el usuario (ver `onAutocierrePosible`).
   *
   *  No hace nada si el trazo ya no está: entre la pregunta y la respuesta el
   *  usuario pudo cancelar, cambiar de herramienta o cerrar la forma a mano. */
  terminarTrazoAbierto(): void {
    if (this._vertices.length < 2) return;
    const puntos = this._puntosDelTrazo();
    this._cancelarTrazo();
    this._opciones.onTrazo?.(puntos, false);
  }

  /** ¿Hay un trazo en curso? Lo pregunta quien confirma un autocierre, para no
   *  abrir un modal sobre un dibujo que ya no existe. */
  get hayTrazo(): boolean {
    return this._vertices.length > 0;
  }

  private _puntosDelTrazo(): Position[] {
    return this._vertices.map((v) => [v.lng, v.lat] as Position);
  }

  /** ¿Este punto es uno de los vértices del padre? Se compara por coordenada y
   *  no por píxeles: el snap ya dejó el punto EXACTAMENTE sobre el vértice, así
   *  que lo que se mide es si eso pasó — con el margen del redondeo a 7
   *  decimales y nada más. Un punto "cerca" de un vértice no sirve: el servidor
   *  lo busca en el anillo del padre y no lo encontraría. */
  private _esVerticeConocido(latlng: L.LatLng): boolean {
    return this._verticesAutocierre.some((v) => this._mismoVertice(v, latlng));
  }

  private _mismoVertice(a: L.LatLng, b: L.LatLng): boolean {
    return (
      Math.abs(a.lat - b.lat) < TOLERANCIA_VERTICE_GRADOS &&
      Math.abs(a.lng - b.lng) < TOLERANCIA_VERTICE_GRADOS
    );
  }

  /** ¿El segmento que iría del último vértice a `destino` cruza algún tramo
   *  ya trazado?
   *
   *  Se compara contra todos los segmentos MENOS el último, porque ese
   *  comparte su extremo final con el nuevo: se tocan en el vértice de unión
   *  por definición, y eso no es un cruce.
   *
   *  Es el mismo problema que `_seCruza` resuelve sobre el anillo completo,
   *  pero adelantado: conviene atajarlo vértice a vértice para que el usuario
   *  no llegue a construir una forma inválida. */
  private _cruzaElTrazo(destino: L.LatLng): boolean {
    if (this._vertices.length < 3) return false;

    const anteriores = this._vertices
      .slice(0, this._vertices.length - 1)
      .map((v) => [v.lng, v.lat] as [number, number]);
    if (anteriores.length < 2) return false;

    const ultimo = this._vertices[this._vertices.length - 1];
    const nuevo: [number, number][] = [
      [ultimo.lng, ultimo.lat],
      [destino.lng, destino.lat],
    ];

    try {
      return (
        turf.lineIntersect(turf.lineString(anteriores), turf.lineString(nuevo))
          .features.length > 0
      );
    } catch {
      // Segmento degenerado (dos clicks en el mismo punto): no bloquea.
      return false;
    }
  }

  /** ¿El anillo se cruza a sí mismo? Reemplaza el `allowIntersection: false`
   *  de `L.Draw.Polygon`, que se fue con leaflet-draw. `turf.kinks` devuelve
   *  los puntos de auto-intersección; con uno solo ya no sirve. */
  private _seCruza(vertices: L.LatLng[]): boolean {
    const anillo: [number, number][] = vertices.map((v) => [v.lng, v.lat]);
    anillo.push(anillo[0]);
    try {
      return turf.kinks(turf.polygon([anillo])).features.length > 0;
    } catch {
      // Anillo degenerado (vértices repetidos, área nula): turf tira. Se trata
      // como inválido, que es lo que es.
      return true;
    }
  }

  /** Parpadeo rojo sobre el trazo que no se pudo cerrar. Va en una capa
   *  aparte y no sobre `_previa`: esa se repinta en cada `mousemove` y el rojo
   *  se perdería al primer movimiento del cursor. */
  private _avisarCruce(): void {
    this._limpiarAvisoCruce();
    this._avisoCruce = L.polygon(this._vertices, {
      ...ERROR_STYLE,
      pane: PANE_AOI,
      interactive: false,
    }).addTo(this._map);
    this._avisoCruceTimer = setTimeout(
      () => this._limpiarAvisoCruce(),
      ERROR_TIMEOUT_MS,
    );
  }

  private _limpiarAvisoCruce(): void {
    if (this._avisoCruceTimer !== null) {
      clearTimeout(this._avisoCruceTimer);
      this._avisoCruceTimer = null;
    }
    if (this._avisoCruce) {
      this._map.removeLayer(this._avisoCruce);
      this._avisoCruce = null;
    }
  }

  private _cancelarTrazo(): void {
    // No hay nada que borrar en la base: el trazo nunca llegó a ella. Se tira
    // lo que hay en memoria y las capas que lo dibujaban.
    this._puntoBajada = null;
    this._vertices = [];
    this._primeroEsConocido = false;
    for (const marcador of this._marcadores) this._map.removeLayer(marcador);
    this._marcadores = [];
    this._quitarVertices();
    if (this._previa) {
      this._map.removeLayer(this._previa);
      this._previa = null;
    }
    this._previaEsPoligono = false;
    this._ocultarRadar();
    this._limpiarAvisoCruce();
  }

  private _mostrarRadar(latlng: L.LatLng): void {
    this._ocultarRadar();
    this._radar = L.marker(latlng, {
      icon: RADAR_ICON,
      interactive: false,
      keyboard: false,
      zIndexOffset: -1000,
    }).addTo(this._map);
  }

  private _ocultarRadar(): void {
    if (this._radar) {
      this._map.removeLayer(this._radar);
      this._radar = null;
    }
  }

  // --- Click sobre una pieza ----------------------------------------------

  /** Resuelve sobre QUÉ pieza se hizo click y lo avisa. Lo usan Editar y
   *  Borrar.
   *
   *  **Por qué no es un `layer.on("click")`.** Porque una pieza guardada ya no
   *  es una capa de Leaflet: la pinta el WMS, cuyo pane tiene
   *  `pointer-events: none` porque un raster no se puede clickear por feature.
   *  Así que el click llega al mapa y se resuelve contra el modelo con
   *  `turf.booleanPointInPolygon` (`AoiPiezasState.enElPunto`). Es lo que hacía
   *  la app de referencia antes de que el paso 3 tuviera capas propias, y con
   *  unas pocas piezas ni se mide.
   *
   *  El hit-testing es LOCAL a propósito, aunque la geometría que se edita se
   *  lea después por gRPC: un GetFeatureInfo por click agregaría un viaje de red
   *  antes de que pase nada, y el modelo ya tiene las mismas geometrías. */
  private _alClickDePieza(latlng: L.LatLng): void {
    const indice = this._piezas.enElPunto(latlng.lng, latlng.lat);
    // Click al aire: no se deselecciona nada acá. Quien maneja el resalte
    // decide qué hacer con un click afuera.
    if (indice === null) return;

    if (this._herramienta === "edit") this._opciones.onPedirEdicion?.(indice);
    else if (this._herramienta === "delete") {
      this._opciones.onPedirBorrado?.(indice);
    }
  }

  // --- Internos ------------------------------------------------------------

  private _marcarDibujando(activo: boolean): void {
    const contenedor = this._map.getContainer();
    if (activo) contenedor.classList.add(CLASE_DIBUJANDO);
    else contenedor.classList.remove(CLASE_DIBUJANDO);
  }
}
